/**
 * CustomerStripeProfile.jsx
 * Erweitertes Kundenprofil mit Live-Stripe-Daten
 * Tabs: Übersicht | Abonnement & Zahlung | Gutscheine
 */
import React, { useState, useEffect, Component } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  CreditCard, RefreshCw, AlertTriangle, CheckCircle2, Clock,
  XCircle, Download, RotateCcw, Loader2, Ticket, User, Eye,
  TrendingUp, Building2, Landmark,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Gibt den korrekten Token für Superadmin-API-Calls zurück.
 * Wenn eine Impersonation aktiv ist, nutze den originalToken des Superadmins.
 */
function getSuperadminToken() {
  const impData = localStorage.getItem('impersonation');
  if (impData) {
    try {
      const parsed = JSON.parse(impData);
      const original = parsed.originalToken || parsed.original_token;
      if (original) return original;
    } catch {}
  }
  return localStorage.getItem('token');
}

// ── Error Boundary: fängt jeden Render-Crash und zeigt Retry statt weißem Bildschirm
class StripeSafeWrapper extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('[CustomerStripeProfile] Render-Fehler:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center py-12 gap-4 text-center px-6">
          <AlertTriangle className="h-10 w-10 text-amber-500" />
          <p className="font-semibold text-foreground">Anzeige-Fehler</p>
          <p className="text-sm text-muted-foreground">{this.state.error?.message || 'Unbekannter Fehler'}</p>
          <Button size="sm" variant="outline" onClick={() => this.setState({ hasError: false, error: null })}
            className="gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" /> Erneut versuchen
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

const STATUS_COLORS = {
  active:              'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  trialing:            'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  past_due:            'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  canceled:            'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  unpaid:              'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  incomplete:          'bg-yellow-100 text-yellow-800',
  incomplete_expired:  'bg-gray-100 text-gray-600',
};

const INV_COLORS = {
  paid:          'text-green-600',
  open:          'text-red-600',
  draft:         'text-gray-500',
  void:          'text-gray-500',
  uncollectible: 'text-orange-600',
};

export function CustomerStripeProfile({ customer, open, onClose, onImpersonate, promoCodesList = [] }) {
  const [activeTab, setActiveTab]       = useState('stripe');
  const [stripeData, setStripeData]     = useState(null);
  const [loadingStripe, setLoadingStripe] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [addingCredit, setAddingCredit] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [couponInput, setCouponInput]     = useState('');
  const [settingCoupon, setSettingCoupon] = useState(false);

  const headers = { Authorization: `Bearer ${getSuperadminToken()}` };

  useEffect(() => {
    if (open && customer) {
      setActiveTab('stripe');
      loadStripeData();
    }
  }, [open, customer?.id]); // eslint-disable-line

  const loadStripeData = async (forceSync = false) => {
    if (!customer?.id) return;
    setLoadingStripe(true);
    try {
      // Schritt 1: Falls forceSync oder erste Öffnung → Backend-Sync anstoßen
      if (forceSync) {
        try {
          await axios.post(
            `${API}/api/superadmin/customers/${customer.id}/sync-stripe`,
            {}, { headers, timeout: 20000 }
          );
        } catch (syncErr) {
          console.warn('Stripe-Sync Fehler (nicht kritisch):', syncErr?.response?.data?.detail);
        }
      }
      // Schritt 2: Gecachte Daten aus DB laden (schnell, kein Timeout)
      const r = await axios.get(
        `${API}/api/superadmin/customers/${customer.id}/stripe-cached`,
        { headers }
      );
      setStripeData(r.data);
    } catch (e) {
      setStripeData({
        has_data: false,
        has_stripe: false,
        message: e.response?.data?.detail || 'Daten konnten nicht geladen werden.',
        _error: true,
      });
    } finally { setLoadingStripe(false); }
  };

  const handleAddCredit = async () => {
    const amt = parseFloat(creditAmount.replace(',', '.'));
    if (isNaN(amt) || amt === 0) return toast.error('Bitte gültigen Betrag eingeben');
    setAddingCredit(true);
    try {
      const r = await axios.post(`${API}/api/superadmin/customers/${customer.id}/adjust-credit`, {
        amount_eur: amt,
        reason: creditReason || 'Manuelle Anpassung durch Superadmin',
      }, { headers });
      toast.success(r.data.message);
      setCreditAmount(''); setCreditReason('');
      loadStripeData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally { setAddingCredit(false); }
  };

  const handleRetryPayment = async () => {
    try {
      const r = await axios.post(`${API}/api/superadmin/customers/${customer.id}/retry-payment`, {}, { headers });
      toast.success(r.data.message);
      loadStripeData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const handleCancelSub = async (atEnd) => {
    const msg = atEnd ? 'Abo zum Periodenende kündigen?' : 'Abo SOFORT kündigen? (nicht rückgängig!)';
    if (!window.confirm(msg)) return;
    try {
      const r = await axios.post(`${API}/api/superadmin/customers/${customer.id}/cancel-subscription?at_period_end=${atEnd}`, {}, { headers });
      toast.success(r.data.message);
      loadStripeData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleReactivate = async () => {
    try {
      const r = await axios.post(`${API}/api/superadmin/customers/${customer.id}/reactivate-subscription`, {}, { headers });
      toast.success(r.data.message);
      loadStripeData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleApplyPromo = async () => {
    if (!selectedPromo) return toast.error('Promo-Code auswählen');
    setApplyingPromo(true);
    try {
      const r = await axios.post(`${API}/api/superadmin/customers/${customer.id}/apply-promo-code`,
        { promo_code_id: selectedPromo }, { headers });
      toast.success(r.data.message, { duration: 6000 });
      setSelectedPromo('');
      loadStripeData();
    } catch (e) {
      // Fallback: Guthaben-Ansatz
      toast.error(e.response?.data?.detail || 'Fehler beim Anwenden');
    } finally { setApplyingPromo(false); }
  };

  const handleSetCoupon = async (couponIdOverride) => {
    const id = couponIdOverride !== undefined ? couponIdOverride : (couponInput.trim() || null);
    setSettingCoupon(true);
    try {
      const r = await axios.patch(
        `${API}/api/superadmin/customers/${customer.id}/set-subscription-coupon`,
        { coupon_id: id },
        { headers }
      );
      toast.success(r.data.message, { duration: 5000 });
      setCouponInput('');
      loadStripeData(true); // Force-Sync damit stripeData sofort aktuell ist
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = (detail && typeof detail === 'object') ? detail.message : (detail || 'Fehler');
      toast.error(msg);
    } finally { setSettingCoupon(false); }
  };

  if (!customer) return null;

  // Sichere Werte mit Defaults
  const sub = stripeData?.subscription || null;
  const invoices = Array.isArray(stripeData?.invoices) ? stripeData.invoices : [];
  // Nur echte Zahlungsprobleme – NICHT "open" ohne Fehler (SEPA "In Verarbeitung")
  const failedInvoices = invoices.filter(i =>
    i && (
      i.status === 'uncollectible' ||
      (i.status === 'open' && (i.failure_reason_de || i.failure_code || i.failure_reason))
    )
  );

  const TABS = [
    { id: 'overview', label: 'Übersicht', mobileLabel: 'Info', icon: User },
    { id: 'stripe',   label: 'Abo & Zahlung', mobileLabel: 'Abo', icon: CreditCard },
    { id: 'coupons',  label: 'Gutscheine', mobileLabel: 'Gutschein', icon: Ticket },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[92vh] overflow-y-auto p-0">
        <DialogHeader className="px-4 sm:px-6 pt-5 pb-0">
          <DialogTitle className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-lg">
              {(customer.company_name || '?')[0].toUpperCase()}
            </div>
            <div>
              <p className="font-bold text-foreground">{customer.company_name || customer.email}</p>
              <p className="text-xs text-muted-foreground font-normal">{customer.contact_email || customer.email} · #{customer.customer_number}</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Tab-Navigation */}
        <div className="flex border-b border-border mx-4 sm:mx-6 mt-4">
          {TABS.map(({ id, label, mobileLabel, icon: Icon }) => (
            <button key={id} type="button"
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-1 px-2 sm:px-4 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors flex-1 justify-center sm:flex-none sm:justify-start ${
                activeTab === id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <span className="hidden sm:inline">{label}</span>
              <span className="sm:hidden">{mobileLabel}</span>
              {id === 'stripe' && failedInvoices.length > 0 && (
                <span className="ml-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center shrink-0">
                  {failedInvoices.length}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="px-4 sm:px-6 py-4 space-y-3">

          {/* ── TAB: ÜBERSICHT ─────────────────────────────────────────── */}
          {activeTab === 'overview' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  ['Firma', customer.company_name],
                  ['Email', customer.contact_email || customer.email],
                  ['Branche', customer.industry],
                  ['Marke', customer.brand || '–'],
                  ['Kundennr.', `#${customer.customer_number}`],
                  ['Status', customer.status],
                ].map(([label, value]) => (
                  <div key={label} className="bg-muted/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
                    <p className="font-medium truncate">{value || '–'}</p>
                  </div>
                ))}
              </div>
              {/* Aktions-Buttons */}
              <div className="flex flex-wrap gap-2 pt-2">
                {onImpersonate && (
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={onImpersonate}>
                    <Eye className="h-3.5 w-3.5" /> Als Kunde einloggen
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* ── TAB: ABO & ZAHLUNG ────────────────────────────────────── */}
          {activeTab === 'stripe' && (
            <StripeSafeWrapper key={customer?.id}>
            <div className="space-y-3">
              {loadingStripe ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : !stripeData?.has_stripe ? (
                <div className="text-center py-8 space-y-3">
                  {stripeData?._timeout || stripeData?._error ? (
                    <>
                      <AlertTriangle className="h-10 w-10 mx-auto text-amber-500" />
                      <p className="text-sm font-medium">{stripeData.message}</p>
                      <Button size="sm" variant="outline" onClick={loadStripeData} className="gap-1.5">
                        <RefreshCw className="h-3.5 w-3.5" /> Erneut versuchen
                      </Button>
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-10 w-10 mx-auto opacity-30" />
                      <p className="text-sm text-muted-foreground">
                        {stripeData?.message || 'Kein Stripe-Account verknüpft'}
                      </p>
                      {stripeData?.has_stripe && (
                        <Button size="sm" variant="outline" onClick={() => loadStripeData(true)} className="gap-1.5">
                          <RefreshCw className="h-3.5 w-3.5" /> Jetzt synchronisieren
                        </Button>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <>
                  {/* ⚠️ Zahlungsproblem-Banner */}
                  {failedInvoices.length > 0 && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-red-800 dark:text-red-300 text-sm">
                            {failedInvoices.length} Zahlungsproblem{failedInvoices.length > 1 ? 'e' : ''}
                          </p>
                          {failedInvoices.map(inv => (
                            <div key={inv.id} className="mt-2 text-xs text-red-700 dark:text-red-400 space-y-0.5">
                              <div><strong>{inv.date}:</strong> {inv.amount_display}</div>
                              {(inv.failure_reason_de || inv.failure_reason) && (
                                <div className="bg-red-100 dark:bg-red-900/30 rounded px-2 py-1 font-medium">
                                  Ursache: {inv.failure_reason_de || inv.failure_reason}
                                </div>
                              )}
                              {inv.attempt_count > 1 && (
                                <div className="text-red-500">{inv.attempt_count}× versucht</div>
                              )}
                            </div>
                          ))}
                          <Button size="sm" className="mt-3 w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white h-8 text-xs gap-1"
                            onClick={handleRetryPayment}>
                            <RotateCcw className="h-3 w-3" /> Zahlung erneut versuchen
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Guthaben + Zahlungsmethode nebeneinander */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className={`flex items-center justify-between p-3 rounded-xl border ${
                      stripeData.credit_balance_eur > 0
                        ? 'border-violet-200 bg-violet-50 dark:bg-violet-900/20'
                        : 'border-border bg-muted/30'
                    }`}>
                      <div>
                        <p className="text-xs text-muted-foreground mb-0.5">Stripe-Guthaben</p>
                        <p className={`text-lg font-bold ${stripeData.credit_balance_eur > 0 ? 'text-violet-700 dark:text-violet-400' : ''}`}>
                          {stripeData.credit_balance_display}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">Wird bei nächster Rechnung verrechnet</p>
                      </div>
                      <Ticket className={`h-7 w-7 shrink-0 ${stripeData.credit_balance_eur > 0 ? 'text-violet-500' : 'text-muted-foreground/30'}`} />
                    </div>

                    {/* Zahlungsmethode */}
                    <div className="flex items-center justify-between p-3 rounded-xl border border-border bg-muted/30">
                      <div>
                        <p className="text-xs text-muted-foreground mb-0.5">Zahlungsmethode</p>
                        {stripeData.payment_method ? (
                          <p className="text-sm font-semibold">{stripeData.payment_method.label}</p>
                        ) : (
                          <p className="text-sm text-muted-foreground">–</p>
                        )}
                      </div>
                      {stripeData.payment_method?.type === 'sepa'
                        ? <Landmark className="h-7 w-7 shrink-0 text-muted-foreground/40" />
                        : <CreditCard className="h-7 w-7 shrink-0 text-muted-foreground/40" />
                      }
                    </div>
                  </div>

                  {/* Abo-Status */}
                  {sub ? (
                    <div className="border border-border rounded-xl p-4 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs text-muted-foreground">Abonnement</p>
                          <p className="font-semibold truncate">{sub.plan_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {sub.plan_amount_eur > 0 ? `${sub.plan_amount_eur.toFixed(2)} €/Monat` : '0,00 €/Monat'}
                          </p>
                        </div>
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${STATUS_COLORS[sub.status] || 'bg-gray-100 text-gray-600'}`}>
                          {sub.status_de || sub.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {sub.trial_end && (
                          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2">
                            <p className="text-muted-foreground">Trial endet</p>
                            <p className="font-semibold text-blue-700 dark:text-blue-400">{sub.trial_end}</p>
                          </div>
                        )}
                        {sub.current_period_end && (
                          <div className="bg-muted/50 rounded-lg p-2">
                            <p className="text-muted-foreground">Nächste Zahlung</p>
                            <p className="font-semibold">{sub.current_period_end}</p>
                          </div>
                        )}
                        {sub.discount && (
                          <div className="col-span-2 bg-green-50 dark:bg-green-900/20 rounded-lg p-2">
                            <p className="text-muted-foreground">Aktiver Rabatt</p>
                            <p className="font-semibold text-green-700 dark:text-green-400">{sub.discount.name}: {sub.discount.label}</p>
                          </div>
                        )}
                        {sub.cancel_at_period_end && (
                          <div className="col-span-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg p-2">
                            <p className="text-orange-700 dark:text-orange-400 font-medium text-xs">Kündigung zum Periodenende geplant</p>
                          </div>
                        )}
                      </div>
                      {/* Abo-Aktionen – auf Mobile gestapelt */}
                      <div className="flex flex-col sm:flex-row gap-2 pt-1 border-t border-border">
                        {sub.cancel_at_period_end ? (
                          <Button size="sm" variant="outline" className="gap-1 text-xs h-8 w-full sm:w-auto" onClick={handleReactivate}>
                            <CheckCircle2 className="h-3 w-3 text-green-600" /> Kündigung zurückziehen
                          </Button>
                        ) : (
                          <>
                            <Button size="sm" variant="outline" className="gap-1 text-xs h-8 w-full sm:w-auto text-orange-600 border-orange-200"
                              onClick={() => handleCancelSub(true)}>
                              <Clock className="h-3 w-3" /> Zum Periodenende kündigen
                            </Button>
                            <Button size="sm" variant="outline" className="gap-1 text-xs h-8 w-full sm:w-auto text-destructive"
                              onClick={() => handleCancelSub(false)}>
                              <XCircle className="h-3 w-3" /> Sofort kündigen
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-sm text-muted-foreground border border-border rounded-xl">
                      Kein aktives Abonnement gefunden
                    </div>
                  )}

                  {/* Guthaben anpassen → jetzt im Stripe Control Center */}
                  <div className="border border-amber-200 dark:border-amber-800 rounded-xl p-4 bg-amber-50 dark:bg-amber-900/10">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex-shrink-0">
                        <svg className="h-4 w-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">Finanzaktionen im Stripe Control Center</p>
                        <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                          Guthaben vergeben, Rückerstattungen und einmalige Rechnungen werden jetzt zentral im Stripe Control Center verwaltet.
                        </p>
                        <a
                          href="/superadmin/stripe?tab=customers"
                          className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium text-amber-800 dark:text-amber-300 hover:underline"
                        >
                          → Zu Finance-Aktionen im Stripe Control Center
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Rechnungshistorie */}
                  <div className="space-y-2">
                    <p className="text-sm font-semibold">Letzte Rechnungen</p>
                    {invoices.length === 0 ? (
                      <p className="text-xs text-muted-foreground py-2">Keine Rechnungen vorhanden</p>
                    ) : invoices.map(inv => (
                      <div key={inv.id}
                        className={`p-3 rounded-lg border text-sm ${
                          (inv.status === 'uncollectible' ||
                           (inv.status === 'open' && (inv.failure_reason_de || inv.failure_code || inv.failure_reason)))
                            ? 'border-red-200 bg-red-50 dark:bg-red-900/10'
                            : 'border-border'
                        }`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-xs text-muted-foreground">{inv.number || inv.id?.slice(0, 10)}</span>
                              <span className="text-xs text-muted-foreground">{inv.date}</span>
                              <span className={`text-xs font-medium ${INV_COLORS[inv.status] || 'text-muted-foreground'}`}>
                                {inv.status_de}
                              </span>
                            </div>
                            {(inv.failure_reason_de || inv.failure_reason) && (
                              <div className="mt-1 text-xs text-red-600 bg-red-50 dark:bg-red-900/20 rounded px-2 py-1">
                                Fehler: {inv.failure_reason_de || inv.failure_reason}
                                {inv.attempt_count > 1 && ` (${inv.attempt_count}× versucht)`}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="font-semibold text-sm">{inv.amount_display}</span>
                            {inv.pdf_url && (
                              <a href={inv.pdf_url} target="_blank" rel="noopener noreferrer"
                                className="text-primary hover:underline">
                                <Download className="h-3.5 w-3.5" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Button variant="ghost" size="sm" className="gap-1.5 text-xs w-full justify-center" onClick={() => loadStripeData(true)}>
                    <RefreshCw className="h-3 w-3" />
                    Jetzt von Stripe synchronisieren
                    {stripeData?.sync_age && <span className="text-muted-foreground ml-1">({stripeData.sync_age})</span>}
                  </Button>
                </>
              )}
            </div>
            </StripeSafeWrapper>
          )}

          {/* ── TAB: GUTSCHEINE ──────────────────────────────────────────── */}
          {activeTab === 'coupons' && (
            <div className="space-y-3">
              {/* ── Aktiver Coupon auf Abo-Ebene (aus stripe_discount_info) ── */}
              {sub?.discount ? (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-green-800 dark:text-green-300 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" /> Aktiver Coupon auf Abo
                    </p>
                    {sub.discount.valid === false && (
                      <span className="text-xs bg-red-100 text-red-700 rounded px-2 py-0.5">Abgelaufen</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-white dark:bg-green-950/30 rounded-lg p-2.5">
                      <p className="text-muted-foreground">Coupon-ID</p>
                      <p className="font-bold mt-0.5 font-mono">{sub.discount.coupon_id || sub.discount.name}</p>
                    </div>
                    <div className="bg-white dark:bg-green-950/30 rounded-lg p-2.5">
                      <p className="text-muted-foreground">Rabatt</p>
                      <p className="font-semibold mt-0.5 text-green-700">
                        {sub.discount.percent_off ? `${sub.discount.percent_off}%` :
                         sub.discount.amount_off ? `${(sub.discount.amount_off/100).toFixed(2)} €` : '–'}
                      </p>
                    </div>
                    <div className="bg-white dark:bg-green-950/30 rounded-lg p-2.5">
                      <p className="text-muted-foreground">Laufzeit</p>
                      <p className="font-semibold mt-0.5">{sub.discount.duration_label || sub.discount.label || '–'}</p>
                    </div>
                    {sub.discount.discount_start && (
                      <div className="bg-white dark:bg-green-950/30 rounded-lg p-2.5">
                        <p className="text-muted-foreground">Aktiv seit</p>
                        <p className="font-semibold mt-0.5">{sub.discount.discount_start}</p>
                      </div>
                    )}
                  </div>
                  {/* Entfernen-Button */}
                  <Button size="sm" variant="outline"
                    disabled={settingCoupon}
                    onClick={() => { if (window.confirm('Coupon vom Abo entfernen?')) handleSetCoupon(null); }}
                    className="text-xs text-destructive border-destructive/40 hover:bg-destructive/10 w-full sm:w-auto gap-1.5"
                    data-testid="coupon-remove-btn"
                  >
                    {settingCoupon ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />}
                    Coupon entfernen
                  </Button>
                </div>
              ) : (
                <div className="text-center py-4 text-sm text-muted-foreground border border-border rounded-xl">
                  <Ticket className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  Kein Coupon auf Abo-Ebene aktiv
                </div>
              )}

              {/* ── Coupon direkt auf Abo setzen/ersetzen ── */}
              <div className="border border-border rounded-xl p-4 space-y-3">
                <p className="text-sm font-semibold flex items-center gap-2">
                  <Ticket className="h-4 w-4 text-violet-600" />
                  Coupon auf Abo setzen / ersetzen
                </p>
                <p className="text-xs text-muted-foreground">
                  Setzt einen Stripe-Coupon direkt auf Subscription-Ebene. Ersetzt einen vorhandenen Coupon.
                </p>

                {/* Schnellauswahl bekannte Coupons */}
                <div className="flex flex-wrap gap-2">
                  {['AFX100','ALI100','DAUER50','JET33'].map(c => (
                    <button key={c} type="button"
                      onClick={() => setCouponInput(c)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        couponInput === c
                          ? 'bg-violet-600 text-white border-violet-600'
                          : 'border-border hover:border-violet-400 text-muted-foreground'
                      }`}
                    >{c}</button>
                  ))}
                </div>

                <input
                  type="text"
                  placeholder="Coupon-ID eingeben (z.B. AFX100)"
                  value={couponInput}
                  onChange={e => setCouponInput(e.target.value.toUpperCase())}
                  className="w-full h-9 rounded-lg border border-border bg-background px-3 text-sm font-mono"
                  data-testid="coupon-id-input"
                />
                <Button size="sm"
                  disabled={!couponInput.trim() || settingCoupon}
                  onClick={() => handleSetCoupon()}
                  className="gap-1.5 bg-violet-600 hover:bg-violet-700 text-white w-full sm:w-auto"
                  data-testid="coupon-set-btn"
                >
                  {settingCoupon ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ticket className="h-3.5 w-3.5" />}
                  Coupon setzen / ersetzen
                </Button>
              </div>

              {/* ── Älteres Promo-Code (Guthaben) Feature ── */}
              <details className="border border-border rounded-xl">
                <summary className="px-4 py-3 text-sm text-muted-foreground cursor-pointer hover:text-foreground">
                  Promo-Code als Guthaben vergeben (ältere Methode)
                </summary>
                <div className="px-4 pb-4 pt-2 space-y-3">
                  <p className="text-xs text-muted-foreground">
                    Bucht einmalig Guthaben auf dem Stripe-Kundenkonto (wird von nächster Rechnung abgezogen).
                  </p>
                  <select value={selectedPromo} onChange={e => setSelectedPromo(e.target.value)}
                    className="w-full h-10 rounded-xl border border-border bg-background px-3 text-sm"
                    style={{ fontSize: '16px' }}>
                    <option value="">Code auswählen...</option>
                    {promoCodesList.filter(pc => pc.active).map(pc => (
                      <option key={pc.id} value={pc.id}>{pc.code} – {pc.discount_label}</option>
                    ))}
                  </select>
                  <Button size="sm" disabled={!selectedPromo || applyingPromo}
                    onClick={handleApplyPromo}
                    className="gap-1.5 bg-violet-600 hover:bg-violet-700 text-white w-full sm:w-auto">
                    {applyingPromo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ticket className="h-3.5 w-3.5" />}
                    Gutschein als Guthaben vergeben
                  </Button>
                </div>
              </details>

              {/* Guthaben-Status */}
              {stripeData && (
                <div className="flex items-center justify-between py-3 px-4 text-sm border border-border rounded-xl">
                  <span className="text-muted-foreground">Aktuelles Stripe-Guthaben</span>
                  <strong className="text-foreground">{stripeData.credit_balance_display}</strong>
                </div>
              )}

              {!stripeData?.has_stripe && (
                <p className="text-xs text-center text-muted-foreground">Bitte erst &quot;Jetzt synchronisieren&quot; klicken</p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
